import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  styled,
  InputAdornment,
  Divider,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import CustomModal from '../Common/CustomModal';
import StyledBackButton from '../Common/StyledBackButton';
import StyledButton from '../Common/StyledButton';
import SearchInput from '../Common/SearchInput';
import { userGroupsActions } from '../../redux/userGroups/userGroupsSlice';
import { useAppSelector } from '../../redux/hooks';
import { chatSlimScrollbarStyles } from '../../utils/styles';
import NameCell from '../Table/NameCell';

const StyledCheckbox = styled(Checkbox)({
  color: '#6CA3AC',
  '&.Mui-checked': {
    color: '#6CA3AC',
  },
});

const StyledGrid = styled(Grid)(() => ({
  maxHeight: 198,
  background: `linear-gradient(0deg, rgba(124, 174, 190, 0.2), rgba(124, 174, 190, 0.2))`,
  gap: '11px',
}));

interface AddRemoveMemberModalProps {
  isAdd?: boolean;
  userGroupId: string;
  name: string;
  isOpen: boolean;
  handleDismiss: () => void;
}

const AddRemoveMemberModal = ({
  isAdd,
  isOpen,
  userGroupId,
  name,
  handleDismiss,
}: AddRemoveMemberModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [selectedCoaches, setSelectedCoaches] = useState<string[]>([]);
  const [noResults, setNoResults] = useState<boolean>(false);

  const {
    clients,
    coaches,
    fetchCoachesAndClientsLoading,
    addRemoveMembersLoading,
    addRemoveMembersSuccess,
  } = useAppSelector((state) => state.userGroups);

  const fetchCoachAndClients = () => {
    dispatch(
      userGroupsActions.fetchCoachesAndClientsRequest({
        filterText: searchTerm,
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
      })
    );
  };

  useEffect(() => {
    const newSelectedClients: string[] = [];
    clients.forEach((member) => {
      if (userGroupId === member.userGroupId && !newSelectedClients.includes(member.id)) {
        newSelectedClients.push(member.id);
      }
    });
    setSelectedClients(newSelectedClients);

    const newSelectedCoaches: string[] = [];
    coaches.forEach((member) => {
      if (userGroupId === member.userGroupId && !newSelectedCoaches.includes(member.id)) {
        newSelectedCoaches.push(member.id);
      }
    });
    setSelectedCoaches(newSelectedCoaches);
  }, [clients, coaches]);

  useEffect(() => {
    fetchCoachAndClients();
  }, []);

  const debouncedFetchCoachAndClients = useCallback(
    debounce(({ filterText }: { filterText: string }) => {
      dispatch(
        userGroupsActions.fetchCoachesAndClientsRequest({
          filterText,
          organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
        })
      );
    }, 1000),
    [dispatch]
  );

  const handleChangeSearchTerm = (newValue: string) => {
    setSearchTerm(newValue);
    debouncedFetchCoachAndClients({ filterText: newValue });
  };

  useEffect(() => {
    if (!fetchCoachesAndClientsLoading && clients.length === 0 && coaches.length === 0) {
      setNoResults(true);
    } else {
      setNoResults(false);
    }
  }, [clients, coaches, fetchCoachesAndClientsLoading]);

  useEffect(() => {
    if (!fetchCoachesAndClientsLoading && clients.length === 0 && coaches.length === 0) {
      setNoResults(true);
    } else {
      setNoResults(false);
    }
  }, [clients, coaches, fetchCoachesAndClientsLoading]);

  const handleOnSubmit = () => {
    dispatch(
      userGroupsActions.addRemoveMembers({
        id: userGroupId,
        coachIds: selectedCoaches,
        clientIds: selectedClients,
      })
    );
  };

  useEffect(
    () => () => {
      debouncedFetchCoachAndClients.cancel();
    },
    [debouncedFetchCoachAndClients]
  );

  useEffect(() => {
    if (addRemoveMembersSuccess) {
      setSelectedClients([]);
      setSelectedCoaches([]);
    }
  }, [addRemoveMembersSuccess]);

  return (
    <CustomModal isOpen={isOpen} width={391}>
      <Box width={391} maxHeight={820}>
        <StyledGrid container gap="11px" textAlign="center" py={4.5} height="300px">
          <Grid item xs={12}>
            <Box component="img" src="assets/icons/members.svg" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="title2Bold" color="#6599A5">
              {t(
                isAdd
                  ? 'userGroups.addRemoveMemberModal.titleAdd'
                  : 'userGroups.addRemoveMemberModal.title'
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subheadRegular" color="#6599A5">
              {`${name} (${selectedClients.length + selectedCoaches.length})`}
            </Typography>
          </Grid>
        </StyledGrid>
        <Box px={3} py={2}>
          <SearchInput
            type="text"
            placeholder={t('clientTable.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => handleChangeSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {fetchCoachesAndClientsLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <img src="/assets/icons/magnifer.svg" alt="search" />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider sx={{ backgroundColor: '#7CAEBE80' }} />

        <Box px={3} py={2}>
          {noResults && (
            <Grid
              container
              sx={{
                height: '42vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img src="/assets/icons/sad-circle.svg" alt="no-results" width={32} height={32} />
              <Typography variant="calloutRegular" color="#2A3C67">
                {t('userGroups.addRemoveMemberModal.noResult')}
              </Typography>
            </Grid>
          )}
          <Grid container maxHeight="38vh" sx={{ overflowY: 'auto', ...chatSlimScrollbarStyles }}>
            {clients.map((member) => (
              <Grid item xs={12} key={member.id}>
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={selectedClients.includes(member.id)}
                      onChange={() => {
                        const includes = selectedClients.includes(member.id);
                        let newValue: string[] = [];
                        if (includes) {
                          newValue = selectedClients.filter((i) => i !== member.id);
                        } else {
                          newValue = [...selectedClients, member.id];
                        }
                        setSelectedClients(newValue);
                      }}
                      inputProps={{ 'aria-label': 'controlled' }}
                      checkedIcon={
                        <Box component="img" src="/assets/icons/check-box-checked-large.svg" />
                      }
                      icon={<Box component="img" src="/assets/icons/check-box-large.svg" />}
                    />
                  }
                  label={
                    <NameCell
                      avatar={member.profilePic}
                      firstName={member.firstName}
                      lastName={member.lastName}
                    />
                  }
                  labelPlacement="start"
                  sx={{
                    margin: 0,
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                />
              </Grid>
            ))}
            {coaches.map((member) => (
              <Grid item xs={12} key={member.id}>
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={selectedCoaches.includes(member.id)}
                      onChange={() => {
                        const includes = selectedCoaches.includes(member.id);
                        let newValue: string[] = [];
                        if (includes) {
                          newValue = selectedCoaches.filter((i) => i !== member.id);
                        } else {
                          newValue = [...selectedCoaches, member.id];
                        }
                        setSelectedCoaches(newValue);
                      }}
                      inputProps={{ 'aria-label': 'controlled' }}
                      checkedIcon={
                        <Box component="img" src="/assets/icons/check-box-checked-large.svg" />
                      }
                      icon={<Box component="img" src="/assets/icons/check-box-large.svg" />}
                    />
                  }
                  label={
                    <Grid container alignItems="center">
                      <NameCell
                        avatar={member.profilePic}
                        firstName={member.firstName}
                        lastName={member.lastName}
                      />
                      <Typography
                        variant="caption2Bold"
                        color="#2F7D32"
                        padding="2px 8px"
                        sx={{
                          background: `linear-gradient(175.46deg, rgba(87, 177, 90, 0.2) -9.81%, 
                                 rgba(47, 125, 50, 0.2) 96.32%)`,
                          borderRadius: '6px',
                          marginLeft: '8px',
                        }}
                      >
                        {t('userGroups.addRemoveMemberModal.coach')}
                      </Typography>
                    </Grid>
                  }
                  labelPlacement="start"
                  sx={{
                    margin: 0,
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        <Divider sx={{ backgroundColor: '#7CAEBE80' }} />
        <Box px={3} py={2}>
          <Grid item xs={12} display="flex" gap="20px" maxHeight={48}>
            <StyledBackButton fullWidth onClick={handleDismiss} disabled={addRemoveMembersLoading}>
              {t(isAdd ? 'common.skip' : 'common.dismiss')}
            </StyledBackButton>
            <StyledButton
              fullWidth
              onClick={handleOnSubmit}
              loading={addRemoveMembersLoading}
              loadingText={t('common.saving')}
            >
              {t('common.saveChanges')}
            </StyledButton>
          </Grid>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default AddRemoveMemberModal;
